import * as React from "react";
import PropTypes from "prop-types";
import { TextField } from "@material-ui/core";
import { styled } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { AutoSizer, List } from "react-virtualized";

const ROW_HEIGHT = 40;
const MAX_VISIBLE_ROWS = 8;

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"] .MuiAutocomplete-input':
    { padding: "5.5px 4px" },

  "& .MuiAutocomplete-popupIndicator::before": {
    content: "''",
    height: 24,
    width: 1,
    left: -1,
    position: "absolute",
    background: theme.palette.grey[300],
    borderRadius: 0,
  },

  "& .MuiAutocomplete-popupIndicator.MuiAutocomplete-popupIndicatorOpen::before":
    {
      content: "''",
      height: 24,
      width: 1,
      left: "100%",
      position: "absolute",
      background: theme.palette.grey[300],
      borderRadius: 0,
    },

  "& .MuiAutocomplete-popupIndicator": {
    position: "relative",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  "& .MuiAutocomplete-clearIndicator": {
    marginRight: 2,
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
}));

const ListboxComponent = React.forwardRef(function ListboxComponent(
  { children, rowHeight = ROW_HEIGHT, ...rest },
  ref,
) {
  const items = React.Children.toArray(children);
  const itemCount = items.length;
  const listHeight = Math.min(
    itemCount * rowHeight,
    MAX_VISIBLE_ROWS * rowHeight,
  );

  return (
    <div
      ref={ref}
      {...rest}
      style={{ ...rest.style, height: listHeight, padding: 0 }}
    >
      <AutoSizer>
        {({ width }) => (
          <List
            height={listHeight}
            width={width}
            rowHeight={rowHeight}
            rowCount={itemCount}
            rowRenderer={({ index, key, style }) => (
              <div key={key} style={style}>
                {items[index]}
              </div>
            )}
          />
        )}
      </AutoSizer>
    </div>
  );
});

ListboxComponent.propTypes = {
  children: PropTypes.node,
  rowHeight: PropTypes.number,
};

export default function VirtualizedAutocomplete({
  getOptionLabel,
  getOptionValue,
  onChange,
  options,
  renderInput,
  renderOption,
  rowHeight = ROW_HEIGHT,
  value,
  ...rest
}) {
  const selectedOption = React.useMemo(() => {
    if (value == null) return null;
    return options.find((opt) => getOptionValue(opt) === value) || null;
  }, [value, options, getOptionValue]);

  const handleChange = React.useCallback(
    (event, newValue) => {
      onChange(newValue);
    },
    [onChange],
  );

  const ListboxWithRowHeight = React.useMemo(
    () =>
      React.forwardRef(function ListBoxComponent(props, ref) {
        return <ListboxComponent {...props} ref={ref} rowHeight={rowHeight} />;
      }),
    [rowHeight],
  );

  return (
    <StyledAutocomplete
      getOptionLabel={getOptionLabel}
      getOptionSelected={(option, val) =>
        val ? getOptionValue(option) === getOptionValue(val) : false
      }
      ListboxComponent={ListboxWithRowHeight}
      onChange={handleChange}
      options={options}
      renderInput={
        renderInput ||
        ((params) => <TextField {...params} variant="outlined" size="small" />)
      }
      renderOption={renderOption}
      value={selectedOption}
      {...rest}
    />
  );
}

VirtualizedAutocomplete.propTypes = {
  getOptionLabel: PropTypes.func.isRequired,
  getOptionValue: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  renderInput: PropTypes.func,
  renderOption: PropTypes.func,
  rowHeight: PropTypes.number,
  value: PropTypes.any,
};
